import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import PerformanceOverview from './dashboard/PerformanceOverview';
import OrganicSearch from './dashboard/OrganicSearch';
import GoogleBusinessProfile from './dashboard/GoogleBusinessProfile';
import SocialMediaPerformance from './dashboard/SocialMediaPerformance';
import GoogleAds from './dashboard/GoogleAds';
import MetaAds from './dashboard/MetaAds';
import YouTubeAds from './dashboard/YouTubeAds';
import EmailMarketing from './dashboard/EmailMarketing';
import ContentPlanning from './dashboard/ContentPlanning';
import EditDataModal from './dashboard/EditDataModal';
import { defaultReportData } from '../utils/defaultData';

const { FiEdit2 } = FiIcons;

const Dashboard = ({ selectedClient }) => {
  const [activeSection, setActiveSection] = useState('overview');
  const [reportData, setReportData] = useState(defaultReportData);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  useEffect(() => {
    if (selectedClient) {
      const savedData = localStorage.getItem(`ar_data_${selectedClient.id}`);
      if (savedData) {
        setReportData(JSON.parse(savedData));
      } else {
        setReportData(defaultReportData);
      }
    }
  }, [selectedClient]);

  const handleSaveData = (newData) => {
    const updatedReport = {
      ...reportData,
      [activeSection]: newData
    };
    setReportData(updatedReport);
    localStorage.setItem(`ar_data_${selectedClient.id}`, JSON.stringify(updatedReport));
    setIsEditModalOpen(false);
  };

  const sections = [
    { id: 'overview', label: 'Overview', component: PerformanceOverview },
    { id: 'seo', label: 'Organic Search (SEO)', component: OrganicSearch },
    { id: 'gbp', label: 'Google Business Profile', component: GoogleBusinessProfile },
    { id: 'social', label: 'Social Media', component: SocialMediaPerformance },
    { id: 'googleAds', label: 'Google Ads (PPC)', component: GoogleAds },
    { id: 'metaAds', label: 'Meta Ads (FB & IG)', component: MetaAds },
    { id: 'youtube', label: 'YouTube Advertising', component: YouTubeAds },
    { id: 'email', label: 'Email Marketing', component: EmailMarketing },
    { id: 'content', label: 'Content Planning', component: ContentPlanning }
  ];

  const ActiveComponent = sections.find(s => s.id === activeSection)?.component || PerformanceOverview;
  // Pass the specific slice of data to the component
  const currentSectionData = reportData[activeSection];

  return (
    <div className="h-full flex flex-col md:flex-row">
      {/* Section Navigation */}
      <div className="w-full md:w-64 bg-gray-50 border-r border-gray-200 p-4 overflow-auto">
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">
          Report Sections
        </h2>
        <nav className="flex md:flex-col space-x-2 md:space-x-0 md:space-y-1 overflow-x-auto md:overflow-visible pb-2 md:pb-0">
          {sections.map((section) => (
            <motion.button
              key={section.id}
              whileHover={{ x: 4 }}
              onClick={() => setActiveSection(section.id)}
              className={`flex-shrink-0 w-auto md:w-full text-left px-3 py-2 rounded-lg text-sm transition-all duration-200 whitespace-nowrap ${
                activeSection === section.id
                  ? 'bg-[#00A859] text-white shadow-sm'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {section.label}
            </motion.button>
          ))}
        </nav>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto bg-gray-50/50">
        {!selectedClient ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-gray-400 text-2xl">📊</span>
              </div>
              <h2 className="text-xl font-semibold text-gray-800 mb-2">Select a Client</h2>
              <p className="text-gray-600">Choose a client from the Clients section to view their SEO dashboard.</p>
            </div>
          </div>
        ) : (
          <motion.div
            key={activeSection}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="p-6"
          >
            <div className="mb-6 flex justify-between items-start">
              <div className="flex items-center space-x-4">
                {selectedClient.logo ? (
                  <img src={selectedClient.logo} alt={selectedClient.name} className="w-12 h-12 rounded-lg object-contain bg-white shadow-sm" />
                ) : (
                  <div className="w-12 h-12 bg-gradient-to-br from-[#00A859] to-[#002B60] rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-lg">
                      {selectedClient.name.charAt(0)}
                    </span>
                  </div>
                )}
                <div>
                  <h1 className="text-2xl font-bold text-gray-800">{selectedClient.name}</h1>
                  <p className="text-gray-600">{selectedClient.website}</p>
                </div>
              </div>
              
              <button 
                onClick={() => setIsEditModalOpen(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 shadow-sm transition-all"
              >
                <SafeIcon icon={FiEdit2} className="text-[#00A859]" />
                <span>Edit Data</span>
              </button>
            </div>

            {/* Pass the FULL reportData to components that might need cross-section data, 
                but usually just the slice. For simplicity, we pass specific data prop. */}
            <ActiveComponent client={selectedClient} data={currentSectionData} />
            
            <EditDataModal 
              isOpen={isEditModalOpen}
              onClose={() => setIsEditModalOpen(false)}
              section={activeSection}
              data={currentSectionData}
              onSave={handleSaveData}
            />
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;